import type { OrganisationAccountingCategory, OrganisationTaxRate, Product } from '@my/directory-api';
import { useProducts } from '@my/directory-api/hooks';
import { PRODUCT_QUANTITY_UNITS, type ProductQuantityUnitValue } from '@my/tms-api';
import { Search, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { categoryOptions } from './lib/accountingCategoryOptions';
import { taxRateOptions } from './lib/taxRateOptions';
import { productRowErrors, type CargoProductDraft } from './lib/cargoDefinitionDraft';

export interface CargoProductsPanelProps {
  organisationId: string | null;
  /** Null when the organisation does not use accounting categories (column hidden). */
  accountingCategories: OrganisationAccountingCategory[] | null;
  /** The cargo definition's own category — what an un-overridden product inherits. */
  inheritedCategoryCode: string;
  /** The organisation's tax schemas — the per-product VAT rate chooses from these. */
  taxRates: OrganisationTaxRate[];
  /** The cargo's own VAT rate as shown ("Reduced rate (13.5%)"), or null when it has none. */
  inheritedTaxRateLabel: string | null;
  products: CargoProductDraft[];
  onChange: (next: CargoProductDraft[]) => void;
  disabled?: boolean;
}

/**
 * Opens under the "Products" property chip: link none, one or more of the
 * organisation's products to this cargo type, each with the quantity a load
 * usually is and its unit. A product can be linked to any number of cargo
 * definitions.
 *
 * <p>
 * The list comes from Directory (Settings → Products); what is stored here is
 * the product's id plus a copy of its name. With nothing linked, orders of
 * this type can pick any of the organisation's products.
 * </p>
 */
export function CargoProductsPanel({
  organisationId,
  accountingCategories,
  inheritedCategoryCode,
  taxRates,
  inheritedTaxRateLabel,
  products,
  onChange,
  disabled,
}: CargoProductsPanelProps) {
  const { t } = useTranslation('configuration');
  // Active products only — an inactive one is not something to link now.
  // The list is small (an organisation's own products), so it is filtered here.
  const catalogueQuery = useProducts(organisationId, { status: 'Active' });
  const catalogue = useMemo(() => catalogueQuery.data ?? [], [catalogueQuery.data]);
  const byId = useMemo(() => new Map(catalogue.map((p) => [p.id, p])), [catalogue]);

  const linked = new Set(products.map((p) => p.productId));
  const set = (index: number, patch: Partial<CargoProductDraft>) =>
    onChange(products.map((p, i) => (i === index ? { ...p, ...patch } : p)));

  return (
    <div className="space-y-2.5 rounded-sm border border-slate-200 bg-white p-3">
      <p className="text-[11px] text-slate-500">
        {products.length === 0 ? t('cargoDefinitions.products.anyHint') : t('cargoDefinitions.products.hint')}
      </p>

      <ProductAdder
        catalogue={catalogue.filter((p) => !linked.has(p.id))}
        loaded={catalogueQuery.isSuccess}
        noneAtAll={catalogueQuery.isSuccess && catalogue.length === 0}
        disabled={disabled}
        onPick={(p) =>
          onChange([
            ...products,
            {
              productId: p.id,
              productName: p.name,
              quantity: '',
              unit: '',
              accountingCategoryCode: '',
              taxRateId: '',
            },
          ])
        }
      />

      {products.length > 0 && (
        <div className="overflow-hidden rounded-sm border border-slate-200">
          <table className="w-full text-xs">
            <thead className="bg-slate-100 text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-2 py-1.5 text-left font-semibold">
                  {t('cargoDefinitions.products.table.product')}
                </th>
                <th className="w-32 px-2 py-1.5 text-left font-semibold">
                  {t('cargoDefinitions.products.table.quantity')}
                </th>
                <th className="w-44 px-2 py-1.5 text-left font-semibold">
                  {t('cargoDefinitions.products.table.unit')}
                </th>
                {accountingCategories && (
                  <th className="w-52 px-2 py-1.5 text-left font-semibold">
                    {t('cargoDefinitions.accountingCategory.label')}
                  </th>
                )}
                <th className="w-52 px-2 py-1.5 text-left font-semibold">
                  {t('cargoDefinitions.vat.label')}
                </th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {products.map((row, index) => {
                const product = byId.get(row.productId);
                const errs = productRowErrors(row);
                return (
                  <tr key={row.productId}>
                    <td className="px-2 py-1.5 align-top">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="font-semibold text-slate-800">
                          {product?.name ?? row.productName}
                        </span>
                        {product && <ProductBadges product={product} />}
                        {catalogueQuery.isSuccess && !product && (
                          <span className="text-[10px] italic text-amber-700">
                            {t('cargoDefinitions.products.missing')}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      <input
                        type="text"
                        inputMode="decimal"
                        aria-label={t('cargoDefinitions.products.table.quantity')}
                        value={row.quantity}
                        disabled={disabled}
                        onChange={(e) => set(index, { quantity: e.target.value })}
                        className={`h-7 w-full rounded-sm border px-1.5 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                          errs.quantity ? 'border-red-500' : 'border-slate-300'
                        }`}
                      />
                      {errs.quantity && (
                        <p className="mt-0.5 text-[10px] text-red-600">
                          {t('cargoDefinitions.products.quantityInvalid')}
                        </p>
                      )}
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      <select
                        aria-label={t('cargoDefinitions.products.table.unit')}
                        value={row.unit}
                        disabled={disabled}
                        onChange={(e) =>
                          set(index, { unit: e.target.value as ProductQuantityUnitValue | '' })
                        }
                        className={`h-7 w-full rounded-sm border bg-white px-1 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                          errs.unit ? 'border-red-500' : 'border-slate-300'
                        }`}
                      >
                        <option value="">{t('cargoDefinitions.products.unitPlaceholder')}</option>
                        {PRODUCT_QUANTITY_UNITS.map((u) => (
                          <option key={u} value={u}>
                            {t(`cargoDefinitions.products.units.${u}`)}
                          </option>
                        ))}
                      </select>
                      {errs.unit && (
                        <p className="mt-0.5 text-[10px] text-red-600">
                          {t('cargoDefinitions.products.unitRequired')}
                        </p>
                      )}
                    </td>
                    {accountingCategories && (
                      <td className="px-2 py-1.5 align-top">
                        {/* Empty = inherit: stored as null, so a later change to
                            the cargo's category carries this product with it. */}
                        <select
                          aria-label={t('cargoDefinitions.accountingCategory.label')}
                          value={row.accountingCategoryCode}
                          disabled={disabled}
                          onChange={(e) => set(index, { accountingCategoryCode: e.target.value })}
                          className={`h-7 w-full rounded-sm border border-slate-300 bg-white px-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                            row.accountingCategoryCode ? 'font-semibold text-slate-800' : 'italic text-slate-500'
                          }`}
                        >
                          {categoryOptions(
                            accountingCategories,
                            row.accountingCategoryCode,
                            inheritedCategoryCode
                              ? t('cargoDefinitions.accountingCategory.inherit', { code: inheritedCategoryCode })
                              : t('cargoDefinitions.accountingCategory.inheritNone'),
                            (code) => t('cargoDefinitions.accountingCategory.notInList', { code }),
                          ).map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </td>
                    )}
                    <td className="px-2 py-1.5 align-top">
                      {/* Empty = inherit the cargo's rate (stored as null), so
                          changing the cargo's rate carries this product too. */}
                      <select
                        aria-label={t('cargoDefinitions.vat.label')}
                        value={row.taxRateId}
                        disabled={disabled}
                        onChange={(e) => set(index, { taxRateId: e.target.value })}
                        className={`h-7 w-full rounded-sm border border-slate-300 bg-white px-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                          row.taxRateId ? 'font-semibold text-slate-800' : 'italic text-slate-500'
                        }`}
                      >
                        {taxRateOptions(
                          taxRates,
                          row.taxRateId,
                          inheritedTaxRateLabel
                            ? t('cargoDefinitions.vat.inherit', { rate: inheritedTaxRateLabel })
                            : t('cargoDefinitions.vat.inheritNone'),
                          t('cargoDefinitions.vat.notInList'),
                        ).map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-1 py-1.5 text-right align-top">
                      <button
                        type="button"
                        aria-label={t('cargoDefinitions.products.remove')}
                        title={t('cargoDefinitions.products.remove')}
                        disabled={disabled}
                        onClick={() => onChange(products.filter((_, i) => i !== index))}
                        className="inline-flex h-6 w-6 items-center justify-center rounded-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** UN number, waste code and grade at a glance — which of two similar names is which. */
function ProductBadges({ product }: { product: Product }) {
  const { t } = useTranslation('configuration');
  return (
    <>
      {product.adrUnNumber && (
        <span className="rounded-sm bg-orange-100 px-1 py-0.5 font-mono text-[10px] font-bold text-orange-800">
          {t('products.adr.un', { un: product.adrUnNumber })}
        </span>
      )}
      {product.ewcCode && (
        <span className="rounded-sm bg-slate-200 px-1 py-0.5 font-mono text-[10px] font-bold text-slate-700">
          {product.ewcCode}
        </span>
      )}
      {product.grade !== 'None' && (
        <span className="rounded-sm bg-green-50 px-1 py-0.5 text-[10px] font-medium text-green-700">
          {t(`products.grade.${product.grade}`)}
        </span>
      )}
    </>
  );
}

/** Type-to-filter over the organisation's products not linked yet. */
function ProductAdder({
  catalogue,
  loaded,
  noneAtAll,
  disabled,
  onPick,
}: {
  catalogue: Product[];
  loaded: boolean;
  noneAtAll: boolean;
  disabled?: boolean;
  onPick: (p: Product) => void;
}) {
  const { t } = useTranslation('configuration');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  if (noneAtAll) {
    return (
      <p className="text-[11px] italic text-slate-500">
        {t('cargoDefinitions.products.noneAvailable')}{' '}
        <Link to="/settings/products" className="font-medium not-italic text-purple-600 hover:underline">
          {t('cargoDefinitions.manageProducts')}
        </Link>
      </p>
    );
  }

  const q = query.trim().toLowerCase();
  const unUnprefixed = q.startsWith('un') ? q.slice(2).trim() : q;
  const matches = catalogue.filter(
    (p) =>
      !q ||
      p.name.toLowerCase().includes(q) ||
      (p.code ?? '').toLowerCase().includes(q) ||
      (p.adrUnNumber ?? '').startsWith(unUnprefixed) ||
      (p.ewcCode ?? '').startsWith(q),
  );

  return (
    <div ref={ref} className="relative w-80">
      <Search
        className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
        aria-hidden="true"
      />
      <input
        type="text"
        aria-label={t('cargoDefinitions.products.add')}
        placeholder={t('cargoDefinitions.products.addPlaceholder')}
        value={query}
        disabled={disabled || !loaded}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
        className="w-full rounded-full border border-slate-300 bg-white py-1.5 pl-8 pr-3 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
      />
      {open && loaded && (
        <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-auto rounded-sm border border-slate-300 bg-white shadow-lg">
          {matches.length === 0 ? (
            <p className="px-2.5 py-2 text-xs text-slate-500">
              {q
                ? t('cargoDefinitions.products.noMatches', { q: query.trim() })
                : t('cargoDefinitions.products.allLinked')}
            </p>
          ) : (
            matches.map((p) => (
              <button
                key={p.id}
                type="button"
                // mousedown, not click: the input's blur would otherwise race the pick.
                onMouseDown={(e) => {
                  e.preventDefault();
                  onPick(p);
                  setQuery('');
                }}
                className="flex w-full flex-wrap items-center gap-1.5 border-b border-slate-100 px-2.5 py-1.5 text-left text-xs last:border-b-0 hover:bg-purple-50"
              >
                <span className="font-medium text-slate-800">{p.name}</span>
                {p.code && <span className="text-[10px] text-slate-500">{p.code}</span>}
                <ProductBadges product={p} />
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
